import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Illustration } from '../../components/Illustration';
import { Logo } from '../../components/Logo';
import { cn, getStatusColor } from '../../lib/utils';
import { paidBackPercent } from '../../lib/equipmentInsights';
import { Confetti } from './Confetti';
import { PaidBackBar } from './PaidBack';
import { prefersReducedMotion, useCountUp, usePlayOnView, placeIn } from './motion';
import { gearArt, shortName } from './gear';

// A job wraps. Its estimate's line items lift off the page, the estimate slides
// away, the gear that earned them turns into view, and each line drops onto its
// piece of gear. Each piece then counts up to how much of its cost it has now
// earned back, and anything past 100% gets confetti.

// When each step starts, in ms from play
const AT = { completed: 600, lift: 1300, away: 2100, turn: 2400, drop: 3600, update: 4100 };
const STEPS = ['idle', 'completed', 'lift', 'away', 'turn', 'drop', 'update', 'done'];
const STAGGER = 80; // between lines, and between panels
const FLY_MS = 700;
const TURN_MS = 650;
const COUNT_MS = 1000;
const HOVER = 20; // how far above its panel's top edge a line waits before it drops

const reached = (step, name) => STEPS.indexOf(step) >= STEPS.indexOf(name);

function useSteps(playId, count, instant) {
  const [step, setStep] = useState('idle');
  useEffect(() => {
    if (!playId) return undefined;
    if (instant) {
      setStep('done');
      return undefined;
    }
    setStep('idle');
    const doneAt = AT.update + (count - 1) * STAGGER + COUNT_MS + 900;
    const timers = [...Object.entries(AT), ['done', doneAt]].map(([name, ms]) => setTimeout(() => setStep(name), ms));
    return () => timers.forEach(clearTimeout);
  }, [playId, count, instant]);
  return step;
}

function GearPanel({ line, index, step, instant, playId, slotRef, cardRef }) {
  const { before, after } = line;
  const turned = reached(step, 'turn');
  const [counting, setCounting] = useState(false);
  useEffect(() => {
    if (!reached(step, 'update')) {
      setCounting(false);
      return undefined;
    }
    if (instant) {
      setCounting(true);
      return undefined;
    }
    const t = setTimeout(() => setCounting(true), index * STAGGER);
    return () => clearTimeout(t);
  }, [step, index, instant]);
  const from = paidBackPercent(before.total_income, before.purchase_price);
  const to = paidBackPercent(after.total_income, after.purchase_price);
  const percent = useCountUp(from, to, counting, { ms: COUNT_MS, instant });
  const paidOff = to >= 100;
  const lit = paidOff && counting; // turns inverse once it has counted past 100
  const [celebrate, setCelebrate] = useState(false);
  useEffect(() => {
    if (!counting || !paidOff || instant) {
      setCelebrate(false);
      return undefined;
    }
    const t = setTimeout(() => setCelebrate(true), COUNT_MS * 0.8);
    return () => clearTimeout(t);
  }, [counting, paidOff, instant]);

  return (
    <li ref={cardRef} className="relative [perspective:900px] min-w-0">
      <div
        className={cn(
          'rounded-tile p-3 sm:p-5 flex flex-col items-center gap-2 sm:gap-3 text-center',
          !instant && 'transition-[transform,opacity,background-color,color] ease-[cubic-bezier(.2,.7,.3,1)]',
          turned ? '[transform:rotateY(0deg)] opacity-100' : '[transform:rotateY(-90deg)] opacity-0',
          lit ? 'bg-inverse text-inverse-foreground' : 'bg-background',
        )}
        style={{ transitionDuration: `${TURN_MS}ms`, transitionDelay: instant || reached(step, 'drop') ? '0ms' : `${index * STAGGER}ms` }}
      >
        <Illustration name={gearArt(line.name)} inverse={lit} className="w-[96px] sm:w-[150px] max-w-full aspect-square" />
        <p className="text-[15px]">{shortName(line.name)}</p>
        <div ref={slotRef} className="w-full flex flex-col items-center gap-2">
          <p className="text-3xl sm:text-4xl font-light tracking-tight leading-none">{Math.round(percent)}%</p>
          <PaidBackBar percent={percent} inverse={lit} className="w-full" />
          <p className={cn('text-sm', lit ? 'text-inverse-muted' : 'text-muted-foreground')}>
            {paidOff && counting ? 'paid for itself' : 'paid back'}
          </p>
        </div>
      </div>
      {celebrate && <Confetti key={playId} />}
    </li>
  );
}

export function JobToGear({ wrap, money }) {
  const { job, lines } = wrap;
  const stageRef = useRef(null);
  const docSlots = useRef([]);
  const panelSlots = useRef([]);
  const cards = useRef([]);
  const pills = useRef([]);
  const seen = usePlayOnView(stageRef, 0.4);
  const [instant] = useState(prefersReducedMotion);
  const [playId, setPlayId] = useState(0);
  useEffect(() => {
    if (seen) setPlayId((id) => id + 1);
  }, [seen]);
  const step = useSteps(playId, lines.length, instant);

  // Where each line sits on the estimate, and where it lands on its panel
  const [places, setPlaces] = useState(null);
  const measure = useCallback(() => {
    const stage = stageRef.current;
    if (!stage) return;
    setPlaces(lines.map((_, i) => {
      const doc = docSlots.current[i];
      const panel = panelSlots.current[i];
      const pill = pills.current[i];
      const card = cards.current[i];
      if (!doc || !panel || !pill || !card) return null;
      const d = placeIn(doc, stage);
      const p = placeIn(panel, stage);
      const c = placeIn(card, stage);
      return {
        w: pill.offsetWidth,
        h: pill.offsetHeight,
        doc: { x: d.x, y: d.y + d.h / 2 },
        hover: { x: c.x + c.w / 2, y: c.y - HOVER },
        panel: { x: p.x + p.w / 2, y: p.y + 18 },
      };
    }));
  }, [lines]);
  useLayoutEffect(() => {
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [measure]);

  const status = reached(step, 'completed') ? 'completed' : 'booked';
  const away = reached(step, 'away');

  const pillStyle = (i) => {
    const place = places?.[i];
    if (!place) return { opacity: 0 };
    const { w, h, doc, hover, panel } = place;
    let x = doc.x;
    let y = doc.y - h / 2;
    let scale = 1;
    let opacity = 1;
    if (reached(step, 'lift')) {
      x = hover.x - w / 2;
      y = hover.y - h;
      scale = 0.92;
    }
    if (reached(step, 'drop')) {
      y = panel.y - h / 2;
      scale = 0.7;
    }
    if (reached(step, 'update')) opacity = 0;
    const delay = reached(step, 'update') ? 0 : i * STAGGER;
    return {
      transform: `translate(${x}px, ${y}px) scale(${scale})`,
      opacity,
      transitionDelay: `${delay}ms`,
      transitionDuration: reached(step, 'drop') && !reached(step, 'update') ? '450ms' : `${FLY_MS}ms`,
    };
  };

  return (
    <div className="flex flex-col gap-6">
      <div ref={stageRef} className="relative">
        {/* The estimate, drawn as a sheet of paper with its lines as chunky pills */}
        <div
          className={cn(
            'absolute left-0 right-0 top-0 mx-auto w-full max-w-md z-[1]',
            !instant && 'transition-[transform,opacity] duration-700 ease-in',
            away ? '-translate-x-[120%] -rotate-6 opacity-0' : '-rotate-1 opacity-100',
            instant && 'hidden',
          )}
          aria-hidden={away}
        >
          <div className="rounded-highlight bg-background border-2 border-foreground p-4 sm:p-6 flex flex-col gap-3 sm:gap-5">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-center gap-3 min-w-0">
                <Logo className="w-8 h-8 shrink-0" alt="" />
                <div className="min-w-0">
                  <p className="text-lg leading-tight">Estimate</p>
                  <p className="text-sm text-muted-foreground">{job.job_number}</p>
                </div>
              </div>
              <span className={cn('shrink-0 rounded-pill px-3 py-1 text-sm capitalize transition-colors duration-500', getStatusColor(status))}>
                {status}
              </span>
            </div>
            <div className="h-0.5 bg-foreground/80 rounded-pill" aria-hidden="true" />
            {/* Places for the lines; the pills themselves float above, so they can fly */}
            <ul className="flex flex-col gap-2.5">
              {lines.map((line, i) => (
                <li key={line.name} ref={(el) => { docSlots.current[i] = el; }} className="h-9 sm:h-11" />
              ))}
            </ul>
            <div className="flex justify-end gap-3 items-center" aria-hidden="true">
              <span className="h-2 w-16 rounded-pill bg-foreground/15" />
              <span className="h-2 w-10 rounded-pill bg-foreground/40" />
            </div>
          </div>
        </div>

        {/* The gear the lines go to, turned edge-on until the estimate has gone */}
        <ul
          className="relative grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 lg:gap-4 pt-4 sm:pt-16"
          aria-label="Each line, to the gear that earned it"
        >
          {lines.map((line, i) => (
            <GearPanel
              key={line.name}
              line={line}
              index={i}
              step={step}
              instant={instant}
              playId={playId}
              slotRef={(el) => { panelSlots.current[i] = el; }}
              cardRef={(el) => { cards.current[i] = el; }}
            />
          ))}
        </ul>

        {/* The line items: on the estimate, then off it, then onto their gear */}
        {!instant && lines.map((line, i) => (
          <span
            key={line.name}
            ref={(el) => { pills.current[i] = el; }}
            className="pointer-events-none absolute left-0 top-0 z-[2] flex items-center gap-3 rounded-pill bg-inverse text-inverse-foreground pl-4 pr-3 h-9 sm:h-11 text-sm sm:text-[15px] whitespace-nowrap transition-[transform,opacity] ease-[cubic-bezier(.3,.7,.25,1)]"
            style={pillStyle(i)}
            aria-hidden="true"
          >
            {shortName(line.name)}
            <span className="rounded-pill bg-inverse-foreground text-inverse px-2.5 py-0.5 text-sm">{money(line.income)}</span>
          </span>
        ))}
      </div>

    </div>
  );
}
